import { menulist as getRoleMenuInfo } from "../api/system";
import { menuList as getFullMenuList } from "../api/role";
import store from "../store";
import { buildAuthorizedMenuTree } from "./menuTree";
import {
  getKnownMenuPaths,
  getRouteCode,
  isPublicPath,
} from "./routeAccess";

// 企业端固定产品线。其它姊妹项目只替换这个值，接口调用方式保持一致。
const SERVER_ID = 1;

function isSuccessfulResponse(response) {
  return response && Number(response.code) === 200;
}

function isRolePayload(payload) {
  const ifMap = payload && payload.ifMap;
  return (
    ifMap !== null &&
    typeof ifMap !== "undefined" &&
    (typeof ifMap === "object" || Array.isArray(ifMap))
  );
}

function getPermissionFailureCode(input) {
  const response = input && input.response;
  const status = response && Number(response.status);
  const code = response
    ? Number(response.data && response.data.code)
    : Number(input && input.code);

  if (status === 401 || code === 401 || code === 110) {
    return 110;
  }

  if (status === 403 || code === 403) {
    return 403;
  }

  return 0;
}

function getTokenFingerprint(token) {
  if (!token) {
    return "";
  }

  if (typeof token !== "object") {
    return String(token);
  }

  // Bind the in-memory menu cache to the signed-in identity. A delayed old
  // request must never clear or reuse a newer role's menu after re-login.
  return [
    token.access_token || token.accessToken || token.token || "",
    token.roleId === undefined || token.roleId === null ? "" : token.roleId,
    token.userId || token.userID || token.id || "",
  ].join("|");
}

const permission = {
  loadedMenuFingerprint: "",
  knownMenuPaths: [],

  resetCache() {
    this.loadedMenuFingerprint = "";
    this.knownMenuPaths = [];
  },

  clearUnauthorizedSession(fingerprint) {
    if (getTokenFingerprint(store.getters.get_token) !== fingerprint) {
      return false;
    }

    this.resetCache();
    store.dispatch("set_token", "");
    store.dispatch("set_user", "");
    store.dispatch("set_menu", "");
    return true;
  },

  resolvePermissionFailure(failureCode, fingerprint, path, staticRoutes) {
    if (failureCode === 110) {
      if (this.clearUnauthorizedSession(fingerprint)) {
        return { code: 110 };
      }

      // A stale request belongs to a previous identity; resolve again for the
      // identity that is currently stored instead of redirecting it to Login.
      return this.init(path, staticRoutes);
    }

    // Keep a valid session for a role authorization failure so the guard can
    // show the dedicated 403 page instead of turning it into a login failure.
    if (failureCode === 403) {
      return { code: 403 };
    }

    return { code: 500 };
  },

  init(path, staticRoutes) {
    if (isPublicPath(path)) {
      return Promise.resolve();
    }

    const token = store.getters.get_token;
    if (!token) {
      // Do not reveal whether a private route exists before authentication.
      return Promise.resolve({ code: 110 });
    }

    const fingerprint = getTokenFingerprint(token);
    const cachedMenu = store.getters.get_menu;

    if (
      Array.isArray(cachedMenu) &&
      this.loadedMenuFingerprint === fingerprint &&
      Array.isArray(this.knownMenuPaths)
    ) {
      return Promise.resolve({
        code: getRouteCode(
          path,
          staticRoutes,
          cachedMenu,
          this.knownMenuPaths
        ),
        data: cachedMenu,
      });
    }

    this.resetCache();

    // Keep the enterprise contract: role.menuList returns all menu records,
    // system.menulist returns the current role's ifMap for this serverId.
    return getFullMenuList({ type: 1 }, { skipGlobalError: true })
      .then((menuResponse) => {
        const failureCode = getPermissionFailureCode(menuResponse);
        if (failureCode) {
          return { failureCode: failureCode };
        }

        if (
          !isSuccessfulResponse(menuResponse) ||
          !Array.isArray(menuResponse.data)
        ) {
          return { failure: true };
        }

        return this.getPromise(menuResponse.data, token, fingerprint);
      })
      .then((menuResult) => {
        if (menuResult && menuResult.failureCode) {
          return this.resolvePermissionFailure(
            menuResult.failureCode,
            fingerprint,
            path,
            staticRoutes
          );
        }

        if (!menuResult || menuResult.failure) {
          // Invalid menu/role data is a server error, never a false 404.
          return { code: 500 };
        }

        return {
          code: getRouteCode(
            path,
            staticRoutes,
            menuResult.tree,
            menuResult.knownMenuPaths
          ),
          data: menuResult.tree,
          diagnostics: menuResult.diagnostics,
        };
      })
      .catch((error) => {
        return this.resolvePermissionFailure(
          getPermissionFailureCode(error),
          fingerprint,
          path,
          staticRoutes
        );
      });
  },

  getPromise(menuList, token, fingerprint) {
    const roleId = token && token.roleId;
    const knownMenuPaths = getKnownMenuPaths(menuList);

    if (roleId === undefined || roleId === null || roleId === "") {
      // A valid account without a role sees known backend menu URLs as 403.
      return Promise.resolve({
        tree: [],
        diagnostics: {},
        knownMenuPaths: knownMenuPaths,
      });
    }

    return getRoleMenuInfo(
      { roleId: roleId, serverId: SERVER_ID },
      { skipGlobalError: true }
    )
      .then((roleResponse) => {
        const failureCode = getPermissionFailureCode(roleResponse);
        if (failureCode) {
          return { failureCode: failureCode };
        }

        if (
          !isSuccessfulResponse(roleResponse) ||
          !isRolePayload(roleResponse.data)
        ) {
          return { failure: true };
        }

        const result = buildAuthorizedMenuTree(menuList, roleResponse.data);

        return store.dispatch("set_menu", result.tree).then(() => {
          this.loadedMenuFingerprint = fingerprint;
          this.knownMenuPaths = knownMenuPaths;
          return {
            tree: result.tree,
            diagnostics: result.diagnostics,
            knownMenuPaths: knownMenuPaths,
          };
        });
      })
      .catch((error) => {
        const failureCode = getPermissionFailureCode(error);
        return failureCode
          ? { failureCode: failureCode }
          : { failure: true };
      });
  },
};

export default permission;
